import React, { memo, useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { Collapse, NavLink } from 'reactstrap';
import {
  faBell,
  faBuilding,
  faCalendar,
  faFileExcel,
  faFileInvoiceDollar,
  faMapMarkerAlt,
  faMobileAlt,
  faQuestionCircle,
  faTicketSimple,
  faUser,
} from '@fortawesome/free-solid-svg-icons';
import css from 'styled-jsx/css';

import {
  ASSETS,
  ASSET_MODELS,
  GEOFENCES,
  SUB_ORGS,
  USERS,
  USER_PROFILES,
} from '../../Constants/InventoryResources';
import {
  ASSET_MODEL,
  ASSET_MODEL_READ,
  BILLING,
  BILLING_WRITE,
  CORE,
  CORE_READ,
  GEOFENCE,
  GEOFENCE_READ,
  MAINTENANCE,
  MAINTENANCE_READ,
  ORGANIZATION,
  ORGANIZATION_READ,
  REPORT,
  REPORT_READ,
  USER,
  USER_READ,
  USER_PROFILE_READ,
  USER_PERMISSION,
} from '../../Constants/RBAC';
import { FLEET, MAINTENANCE_REPORT, MAP } from '../../Constants/Routes';
import BrandingContext from '../../Context/BrandingContext';
import CurrentOrgContext from '../../Context/CurrentOrgContext';
import CurrentUserContext from '../../Context/CurrentUserContext';
import RootOrgContext from '../../Context/RootOrgContext';
import { Width } from '../../Context/WidthContext';

import SearchBar from '../Search/SearchBar';
import {
  Menu,
  UserNameAndProfile,
  CollapseButton,
  TopNavLink,
  SubNavLink,
  HelpLink,
  LogoutButton,
} from './MobileHeaderDisplayComponents';
import Can from '../../RBAC/Can';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export default memo(MobileHeader);

const { className: navLinkClass, styles: navLinkStyles } = css.resolve`
  a {
    color: var(--text-muted);
  }
  a:hover,
  .active {
    color: var(--brand-primary);
  }
`;

const initState = {
  collapseApps: false,
  collapseHelp: false,
  collapseMaintenance: false,
  collapseOrganization: false,
  collapseReports: false,
  appsIsOpen: false,
  helpIsOpen: false,
  isOpen: false,
  maintenanceIsOpen: false,
  organizationIsOpen: false,
  reportsIsOpen: false,
};

function MobileHeader({ children, logout }) {
  const [state, updateState] = useState(initState);

  const history = useHistory();
  const location = useLocation();
  const { pathname = '', search = '' } = location || {};

  const { tab } = queryString.parse(search);

  const {
    documentation_url,
    isElevatUi,
    isVersatileUi,
    privacy_policy_url,
    youtube_url,
  } = useContext(BrandingContext);
  const {
    _id: currentOrgID,
    features: {
      apps = [],
      csvExport,
      maintenance_v1,
      maintenance_v2,
      parts,
    } = {},
  } = useContext(CurrentOrgContext) || {};
  const { name: rootOrgName } = useContext(RootOrgContext) || {};
  const user = useContext(CurrentUserContext);
  const width = useContext(Width);

  const usesV1Maintenance = useMemo(
      () => maintenance_v1?.enabled ?? false,
      [maintenance_v1?.enabled]
    ),
    usesV2Maintenance = useMemo(
      () => maintenance_v2?.enabled ?? false,
      [maintenance_v2?.enabled]
    );

  const toggleApps = () => {
    updateState((prevState) => ({
      ...prevState,
      appsIsOpen: true,
      collapseApps: !state.collapseApps,
    }));
  };

  const toggleHelp = () => {
    updateState((prevState) => ({
      ...prevState,
      collapseHelp: !state.collapseHelp,
      helpIsOpen: true,
    }));
  };

  const toggleHelpMenuState = () => {
    updateState((prevState) => ({
      ...prevState,
      helpIsOpen: !state.helpIsOpen,
      isOpen: false,
    }));
  };

  const toggleMaintenance = () => {
    updateState((prevState) => ({
      ...prevState,
      collapseMaintenance: !state.collapseMaintenance,
      maintenanceIsOpen: true,
    }));
  };

  const toggleOrganization = () => {
    updateState((prevState) => ({
      ...prevState,
      collapseOrganization: !state.collapseOrganization,
      organizationIsOpen: true,
    }));
  };

  const toggleOrganizationMenuState = () => {
    updateState((prevState) => ({
      ...prevState,
      organizationIsOpen: !state.organizationIsOpen,
      isOpen: false,
    }));
  };

  const toggleReports = () => {
    updateState((prevState) => ({
      ...prevState,
      collapseReports: !state.collapseReports,
      reportsIsOpen: true,
    }));
  };

  const toggleSlideMenu = () => {
    updateState((prevState) => ({ ...prevState, isOpen: false }));
  };

  const updateMenuState = (state) => {
    const {
      appsIsOpen,
      helpIsOpen,
      maintenanceIsOpen,
      organizationIsOpen,
      reportsIsOpen,
      isOpen,
    } = state;
    const collapseApps = !appsIsOpen ? false : state.collapseApps;
    const collapseHelp = !helpIsOpen ? false : state.collapseHelp;
    const collapseMaintenance = !maintenanceIsOpen
      ? false
      : state.collapseMaintenance;
    const collapseOrganization = !organizationIsOpen
      ? false
      : state.collapseOrganization;
    const collapseReports = !reportsIsOpen ? false : state.reportsIsOpen;
    updateState((prevState) => ({
      ...prevState,
      collapseApps,
      collapseHelp,
      collapseMaintenance,
      collapseOrganization,
      collapseReports,
      appsIsOpen,
      helpIsOpen,
      isOpen,
      organizationIsOpen,
    }));
  };

  return (
    <div className="h-100">
      {width < 992 ? (
        <Menu
          updateMenuState={updateMenuState}
          organizationIsOpen={state.organizationIsOpen}
          isOpen={state.isOpen}
        >
          <>
            <UserNameAndProfile rootOrgName={rootOrgName} user={user} />
            <SearchBar
              inputClass="col-12 mt-3"
              history={history}
              updateMobileMenuState={updateMenuState}
            />
            <TopNavLink
              icon={faMapMarkerAlt}
              path={MAP}
              isActive={pathname.startsWith(MAP)}
              toggleSlideMenu={toggleSlideMenu}
            >
              Map
            </TopNavLink>
            <CollapseButton
              toggleCollapse={toggleOrganization}
              isCollapsed={state.collapseOrganization}
              icon={faBuilding}
            >
              Organization
            </CollapseButton>
            <Collapse isOpen={state.collapseOrganization}>
              <Can
                no={() => null}
                orgID={currentOrgID}
                perform={CORE_READ}
                resource={CORE}
                yes={() => (
                  <SubNavLink
                    path={`/organization/${ASSETS}`}
                    toggleMenu={toggleOrganizationMenuState}
                    isActive={pathname.startsWith(`/organization/${ASSETS}`)}
                  >
                    Assets
                  </SubNavLink>
                )}
              />
              <Can
                no={() => null}
                orgID={currentOrgID}
                perform={ASSET_MODEL_READ}
                resource={ASSET_MODEL}
                yes={() => (
                  <SubNavLink
                    path={`/organization/${ASSET_MODELS}`}
                    toggleMenu={toggleOrganizationMenuState}
                    isActive={pathname.startsWith(
                      `/organization/${ASSET_MODELS}`
                    )}
                  >
                    Asset Models
                  </SubNavLink>
                )}
              />
              <Can
                no={() => null}
                orgID={currentOrgID}
                perform={GEOFENCE_READ}
                resource={GEOFENCE}
                yes={() => (
                  <SubNavLink
                    path={`/organization/${GEOFENCES}`}
                    toggleMenu={toggleOrganizationMenuState}
                    isActive={pathname.startsWith(`/organization/${GEOFENCES}`)}
                  >
                    Geofences
                  </SubNavLink>
                )}
              />
              <Can
                no={() => null}
                orgID={currentOrgID}
                perform={ORGANIZATION_READ}
                resource={ORGANIZATION}
                yes={() => (
                  <SubNavLink
                    path={`/organization/${SUB_ORGS}`}
                    toggleMenu={toggleOrganizationMenuState}
                    isActive={pathname.startsWith(`/organization/${SUB_ORGS}`)}
                  >
                    Sub Orgs
                  </SubNavLink>
                )}
              />
              <Can
                no={() => null}
                orgID={currentOrgID}
                perform={USER_READ}
                resource={USER}
                yes={() => (
                  <SubNavLink
                    path={`/organization/${USERS}`}
                    toggleMenu={toggleOrganizationMenuState}
                    isActive={pathname.startsWith(`/organization/${USERS}`)}
                  >
                    Users
                  </SubNavLink>
                )}
              />
              <Can
                no={() => null}
                orgID={currentOrgID}
                perform={USER_PROFILE_READ}
                resource={USER_PERMISSION}
                yes={() => (
                  <SubNavLink
                    path={`/organization/${USER_PROFILES}`}
                    toggleMenu={toggleOrganizationMenuState}
                    isActive={pathname.startsWith(
                      `/organization/${USER_PROFILES}`
                    )}
                  >
                    User Profiles
                  </SubNavLink>
                )}
              />
            </Collapse>
            <TopNavLink
              icon={faBell}
              path="/events?tab=alerts"
              isActive={pathname.startsWith('/events')}
              replace={true}
              toggleSlideMenu={toggleSlideMenu}
            >
              Events
            </TopNavLink>
            {usesV1Maintenance ? (
              <Can
                no={() => null}
                orgID={currentOrgID}
                perform={MAINTENANCE_READ}
                resource={MAINTENANCE}
                yes={() => (
                  <CollapseButton
                    icon={faCalendar}
                    isCollapsed={state.collapseMaintenance}
                    toggleCollapse={toggleMaintenance}
                  >
                    Maintenance
                  </CollapseButton>
                )}
              />
            ) : null}
            <Collapse isOpen={state.collapseMaintenance}>
              <SubNavLink
                isActive={tab === 'service'}
                path="/maintenance?tab=service"
                toggleMenu={toggleOrganizationMenuState}
              >
                Dashboard
              </SubNavLink>
              <SubNavLink
                isActive={tab === 'schedules'}
                path="/maintenance?tab=schedules"
                toggleMenu={toggleOrganizationMenuState}
              >
                Schedules
              </SubNavLink>
              {parts ? (
                <SubNavLink
                  isActive={tab === 'parts'}
                  path="/maintenance?tab=parts"
                  toggleMenu={toggleOrganizationMenuState}
                >
                  Parts
                </SubNavLink>
              ) : null}
            </Collapse>
            <Can
              no={() => null}
              orgID={currentOrgID}
              perform={REPORT_READ}
              resource={REPORT}
              yes={() => (
                <CollapseButton
                  icon={faFileExcel}
                  isCollapsed={state.collapseReports}
                  toggleCollapse={toggleReports}
                >
                  Reports
                </CollapseButton>
              )}
            />
            <Collapse isOpen={state.collapseReports}>
              {csvExport?.signals > 0 || csvExport?.onDemand ? (
                <SubNavLink
                  path={`/csv-reports?tab=${
                    csvExport.signals > 0 ? 'scheduled' : 'on-demand'
                  }`}
                  toggleMenu={toggleOrganizationMenuState}
                  isActive={pathname.startsWith('/csv-reports')}
                >
                  CSV Export
                </SubNavLink>
              ) : null}
              <SubNavLink
                path="/fleet"
                toggleMenu={toggleOrganizationMenuState}
                isActive={pathname.startsWith(FLEET)}
              >
                Fleet
              </SubNavLink>
              {usesV2Maintenance ? (
                <Can
                  no={() => null}
                  orgID={currentOrgID}
                  perform={MAINTENANCE_READ}
                  resource={MAINTENANCE}
                  yes={() => (
                    <SubNavLink
                      isActive={pathname.startsWith(MAINTENANCE_REPORT)}
                      path={MAINTENANCE_REPORT}
                      toggleMenu={toggleOrganizationMenuState}
                    >
                      Maintenance
                    </SubNavLink>
                  )}
                />
              ) : null}
              {/* <SubNavLink
                path="/reports?tab=inventory"
                toggleMenu={toggleOrganizationMenuState}
                isActive={pathname.startsWith('/reports')}
              >
                Utilization
              </SubNavLink> */}
            </Collapse>
            {apps.length ? (
              <>
                <CollapseButton
                  icon={faMobileAlt}
                  isCollapsed={state.collapseApps}
                  toggleCollapse={toggleApps}
                >
                  Apps
                </CollapseButton>
                <Collapse isOpen={state.collapseApps}>
                  {apps.includes('engineanalysis') ? (
                    <SubNavLink
                      isActive={pathname.startsWith('/engineanalysis')}
                      path={`/engineanalysis?orgID=${currentOrgID}&viewing=orgsList`}
                      toggleMenu={toggleOrganizationMenuState}
                    >
                      Cummins Diagnostics
                    </SubNavLink>
                  ) : null}
                  {apps.includes('waterpump') ? (
                    <SubNavLink
                      isActive={pathname.startsWith('/watertransfer')}
                      path={`/watertransfer?orgID=${currentOrgID}`}
                      toggleMenu={toggleOrganizationMenuState}
                    >
                      Water Transfer
                    </SubNavLink>
                  ) : null}
                </Collapse>
              </>
            ) : null}
            <TopNavLink
              icon={faUser}
              path="/settings"
              isActive={pathname.startsWith('/settings')}
              toggleSlideMenu={toggleSlideMenu}
            >
              Settings
            </TopNavLink>
            <CollapseButton
              customStyles={{ marginBottom: '1rem' }}
              icon={faQuestionCircle}
              isCollapsed={state.collapseHelp}
              toggleCollapse={toggleHelp}
            >
              Help
            </CollapseButton>
            <Collapse isOpen={state.collapseHelp}>
              <HelpLink url={youtube_url} toggle={toggleHelpMenuState}>
                YouTube
              </HelpLink>
              <HelpLink url={documentation_url} toggle={toggleHelpMenuState}>
                Documentation
              </HelpLink>
              {isVersatileUi ? (
                <HelpLink url={privacy_policy_url} toggle={toggleHelpMenuState}>
                  Privacy Policy
                </HelpLink>
              ) : null}
            </Collapse>
            {isElevatUi ? (
              <NavLink
                className={`p-0 mt-0 text-decoration-none ${navLinkClass}`}
                href="https://elevat.atlassian.net/servicedesk/customer/portals"
              >
                <FontAwesomeIcon icon={faTicketSimple} fixedWidth />
                <span className="ml-4">Support Portal</span>
                {navLinkStyles}
              </NavLink>
            ) : null}
            <Can
              no={() => null}
              orgID={user?.rootOrgID}
              perform={BILLING_WRITE}
              resource={BILLING}
              yes={() => (
                <NavLink
                  className={`${
                    isElevatUi ? 'p-0 mt-3 ' : 'p-0 mt-0 '
                  }text-decoration-none ${navLinkClass}`}
                  href={process.env['BILLING_PORTAL_URL']}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <FontAwesomeIcon icon={faFileInvoiceDollar} fixedWidth />
                  <span className="ml-4">Billing Portal</span>
                  {navLinkStyles}
                </NavLink>
              )}
            />
            <LogoutButton logout={logout} />
          </>
        </Menu>
      ) : null}
      {children}
    </div>
  );
}

MobileHeader.propTypes = { children: PropTypes.node, logout: PropTypes.func };
